import type { DiscountType } from '@/types/invoice'
import type { Field } from '@/types/templates'

export interface LineTax {
  id?: number
  taxName: string        // "IVA", "Retención Honorarios", "ReteIva"
  taxPercentage: number  // 19
  taxableAmount: number
  taxAmount: number
  taxTypeCode: 'TAX' | 'WITHHOLDING'
  // The tax's fiscal code as the API stores it (`tax_type_code`, e.g. "01" IVA or "06" ReteFuente in CO).
  // `taxTypeCode` above is only the kind; the platform's tax classifiers read this one.
  fiscalCode?: string
  /**
   * INTERNAL, never persisted. A withholding's `_baseTracksNet` from the payment flow
   * (`initWithholdingBaseTracking`): whether its base keeps following the line's net (or IVA, for a
   * ReteIva). Tax rows snap to the net whenever the net changes, as in the payment invoice form.
   */
  baseTracksNet?: boolean
  /**
   * INTERNAL, never persisted. True for a withholding seeded from the policy's masters. Seeded rows are
   * read-only in the UI (name + presence), mirroring the payment flow where `tax_type` is fixed and the
   * list is exactly what the policy defines.
   */
  fromPolicy?: boolean
}

export interface AccountingSplit {
  // `undefined` = not chosen yet (a half-built row in the editor). Never 0 — there is no "id 0" in the
  // backend, so a split only reaches the payload once every id is a real value (see renditionPayload).
  costCenterId?: number
  costCenterLabel: string
  // Chart account (cuenta contable), picked directly — same model as the rest of the platform's splits.
  // This is what the backend receives (payload key `account`).
  accountId?: number
  accountLabel: string
  accountCode?: string   // shown as "code | name" like the platform's Splits selectors
  // Answers to the splittable custom fields (keyed by field id), same as the platform's splits.
  customFieldsAnswers?: Record<number, string | undefined>
  percentage: number     // los splits de una línea suman 100
}

export interface RenditionLine {
  id?: number
  itemName: string
  // Product catalog link (same as payment requests): id of the picked `purchases.Product`, or 0 when the
  // user typed a new name (the backend creates it on submit). Undefined on lines not yet touched.
  productId?: number
  productCode?: string
  description?: string
  unitPrice: number
  quantity: number
  discount: number
  total: number
  taxes: LineTax[]
  splits: AccountingSplit[]
  // Requester's part of this line when the invoice is shared; unset otherwise.
  shareQty?: number
  shareTotal?: number
  // Quantity already taken by the other participants of a receipt someone else declared as shared.
  shareTakenQty?: number
  // The line's number on a saved receipt: a part of someone else's receipt points at it.
  number?: number
  // What the api says is left of the line for the requester, in the receipt's currency. It is the cap;
  // `shareTakenQty` only draws the quantity beside it.
  shareAvailable?: number
  // Every live part of this line of a shared receipt, as the api read it, and the line's value then. A correction
  // of the receipt re-spreads them by their share of the line (`spreadLineParts`), as the api does on save.
  shareParts?: RenditionLinePart[]
  sharePartsValue?: number
  // The reader added it so the lines add up to the printed total ("Otros ítems no detallados", "Ajuste de
  // redondeo"). Not the person's: its amounts are locked, and it may be negative.
  isAdjustment?: boolean
}

/** One person's part of a receipt line. `fraction` is its share of the line; an approved part keeps `amount` instead. */
export interface RenditionLinePart {
  userId: number
  name: string
  approved: boolean
  amount: number
  fraction: number
  // This rendition's part, and the receipt owner's (who takes the rounding residue when the line is taken whole).
  mine: boolean
  owner: boolean
}

export interface RenditionSharePerson {
  id: number
  name: string
  image?: string | null
}

/** Fiscal identity of a receipt, used to ask whether someone else already declared it as shared. */
export interface SharedReceiptQuery {
  number: string
  emissionDate: string
  total: number
}

/** Someone else's rendition already declares this receipt as shared. */
export interface SharedReceiptDeclaration {
  invoiceId: number
  // The owner's rendition, where a claim to be added is left; null when the owner no longer has one.
  requestId: number | null
  requestLabel: string
  declaredBy: RenditionSharePerson
  includesMe: boolean
  // The requester's own part of the receipt, still alive in a rendition (a draft or one on its way to approval):
  // they already rendered it there, and taking it here would release it there.
  myLivePart?: { requestId: number; requestLabel: string }
}

/**
 * A declaration and, when it includes the requester, the receipt as its owner saved it with the share
 * already on it: its people and what the others left of each line. The requester's part is claimed on
 * these lines, never on their own reading of the photo.
 */
export interface SharedReceiptFound {
  declaration: SharedReceiptDeclaration
  receipt?: Pick<RenditionInvoice, 'lines' | 'shares' | 'tip' | 'nativeTotal'>
    & Partial<Pick<RenditionInvoice, 'providerId' | 'providerName' | 'number' | 'emissionDate' | 'currency' | 'invoiceType' | 'discountType' | 'discountTotal' | 'documentUrl'>>
}

/** Which of a shared invoice's two views is being edited: the document itself, or the requester's part. */
export type ShareEditMode = 'document' | 'share'

export interface RenditionShare extends RenditionSharePerson {
  // What this person already took in their own rendition, as the api reports it to the owner.
  claimedAmount?: number | null
  claimStatus?: 'CLAIMED' | 'APPROVED' | null
}

/**
 * Lifecycle of a document-backed expense:
 * - `uploading` — file is being sent to S3
 * - `pending`   — uploaded, awaiting async AI extraction (future phase)
 * - `done`      — AI extraction finished and populated the lines
 * - `failed`    — upload (or extraction) failed
 * - `manual`    — no document / user fills the data by hand
 */
export type RenditionAnalysisStatus = 'uploading' | 'pending' | 'done' | 'failed' | 'manual'

/**
 * Fiscal document type a rendition expense defaults to: a receipt ("Boleta"), which is what an employee
 * reimbursement almost always is. THE only default — the selectors and the payload each had their own and
 * disagreed (`SALES_RECEIPT` vs `INVOICE`), so a user who never touched the select saw "Boleta" and saved
 * "Factura". Written into every invoice at build/hydrate time so no caller has to guess.
 */
export const DEFAULT_INVOICE_TYPE = 'SALES_RECEIPT'

/**
 * A settings field where the AI extraction disagreed with what the user typed while the invoice was
 * still processing. Surfaced in the card so the user picks which value to keep. `provider` is special:
 * its `extractedValue` is a name that gets matched against the provider list on resolve (1 match →
 * link; >1 or 0 → the user picks via the provider dialog).
 */
export type RenditionConflictField = 'number' | 'emissionDate' | 'provider'
export interface RenditionFieldConflict {
  field: RenditionConflictField
  /** What the user typed (the default active value; kept until they resolve the conflict). */
  userValue: string
  /** What the extraction returned. */
  extractedValue: string
}

/** Inline notification pushed when an in-line extraction resolves (shown in the rendition step). */
export interface RenditionProcessingNotification {
  id: string
  /** Provider name (or file name) to identify which expense finished. */
  label: string
  kind: 'done' | 'failed'
  /** True when the resolved extraction disagreed with user-entered settings (there are conflicts). */
  hasConflicts: boolean
}

export interface RenditionInvoice {
  id?: number
  /**
   * Set ONLY on an expense built by associating an invoice that already exists in the society (the
   * `PendingInvoiceSelector` path). Carries that Invoice's id, and travels as the payload's own
   * `existing_invoice_id`.
   *
   * Deliberately NOT `id`: in this contract `id` means "an invoice already belonging to THIS rendition,
   * update it in place", and on create the backend ignores it and creates a duplicate. Two keys keep the
   * three cases apart — no key = create new, `existing_invoice_id` = associate a free invoice,
   * `id` = edit in place.
   */
  existingInvoiceId?: number
  // `undefined` = no provider chosen yet. Never 0 — the payload sends the real id or null, never a sentinel.
  providerId?: number
  providerName: string
  /** The issuer's tax id as the document reads it (pool extraction). Only compared, never sent: the backend resolves the provider. */
  providerTaxId?: string
  number: string
  // Fiscal document type (INVOICE, SERVICE_FEE, SALES_RECEIPT, …) — the invoice `type` in the payload.
  // Always set (see DEFAULT_INVOICE_TYPE above). Distinct from `documentType` below, which is the
  // uploaded file's MIME type used for preview.
  invoiceType: string
  emissionDate: string
  dueDate?: string
  // Tip (propina) charged on THIS document, as an amount — not a percentage and not a tax. Adds to the
  // invoice total and travels as the backend's own `tip_amount` (Invoice.tip_amount, which it folds into
  // the total it recomputes). Undefined = no tip.
  tip?: number
  // Free text describing the whole document, distinct from each line's own `description`. It is the
  // invoice's `description` in the payload, and the ERP shows it as the expense's detail.
  description?: string
  currency: string
  // Rate from this invoice's currency to the policy ("home") currency, AS THE BACKEND RESOLVED IT from the
  // policy (preview while editing, detail when loading). Read-only here: the frontend never fetches nor
  // guesses a rate, and never sums at 1:1 when it is missing.
  exchangeRate?: number
  // This invoice's total in the policy currency, computed by the backend alongside `exchangeRate`.
  convertedTotal?: number
  // This invoice's total in its own currency as the backend computed it (preview). For a pool-backed
  // receipt it is the printed total; the wizard never sums lines to display it.
  nativeTotal?: number
  // Invoice-level taxes/withholdings, entered via the same tax dialog as lines (base imponible = invoice net).
  taxes: LineTax[]
  categoryId?: number
  categoryLabel?: string
  subcategoryId?: number
  subcategoryLabel?: string
  areaId?: number
  areaLabel?: string
  lines: RenditionLine[]
  // Document backing this expense (one document = one invoice).
  documentFileId?: number   // id of the TwilioInvoiceFile created by the upload endpoint.
  // The lines the backend holds for a pool-backed receipt as they arrived: the extraction's on a pick, the saved
  // ones on a reopened draft. Edited lines that stop charging what these do are rebuilt on save.
  receivedLines?: RenditionLine[]
  documentUrl?: string      // S3 URL once uploaded; local objectURL while the file is still being sent.
  documentName?: string
  documentType?: string     // MIME type — decides PDF (iframe) vs image (img) preview.
  // `documentUrl` is the owner's photo, borrowed while this expense is the requester's part of the owner's receipt and they
  // have none of their own. It goes back when the declaration does.
  shareOwnerDocument?: boolean
  analysisStatus?: RenditionAnalysisStatus
  // Settings fields where the resolved extraction disagreed with the user's input (in-line processing).
  // Present only until the user resolves them; the user's value stays active meanwhile.
  conflicts?: RenditionFieldConflict[]
  // Explicit "I looked at both documents, it's not a duplicate" — set only after the user picks it in
  // the possible-duplicate modal. Travels as `duplicate_override` and skips the backend's possible-duplicate
  // check for THIS item only; every other item keeps being checked.
  duplicateOverride?: boolean
  // People the document is shared with. The first entry is always the requester of this rendition.
  shares?: RenditionShare[]
  // The requester's part of the tip once typed; unset follows their lines in proportion.
  shareTip?: number
  // What the other parts left of the tip, as the backend counted it; caps the requester's own.
  shareTipAvailable?: number
  // How an invoice that already exists reads its line discounts, and its header discount. The form reads
  // every line discount as a percentage; only the share math, which must match the backend, reads these.
  discountType?: DiscountType
  discountTotal?: number
  // The saved invoice was already shared, so dropping `shares` has to travel as `shared: null`.
  sharedWhenLoaded?: boolean
  // Its currency when loaded: a new one moves the other parts to new rates, which the save confirms first.
  shareCurrencyWhenLoaded?: string
  // Someone else owns this shared receipt: its people and its document are theirs, only the part is edited here.
  shareOwnedByOther?: boolean
  // Other parts hang on this receipt's lines, so its document can't be corrected from this rendition.
  shareDocumentLocked?: boolean
  // Set when another rendition already declared this receipt as shared; its declarer owns `shares`.
  sharedDeclaration?: SharedReceiptDeclaration
  // What the expense had before it became the requester's part of that receipt; back if it stops being shared.
  sharedFrom?: Pick<RenditionInvoice, 'lines' | 'tip' | 'nativeTotal' | 'discountType' | 'discountTotal'>
  // The requester already asked the declarer (by comment) to be added to the receipt.
  sharedClaimSent?: boolean
}

export interface Rendition {
  policyId: number
  description?: string
  categoryId?: number
  subcategoryId?: number
  areaId?: number
  // Request-level cost center (the policy's `field_visibility.cost_center` field). Sent as the top-level
  // `cost_center` id — the backend materializes it as a single 100% header Split, exactly as compras does.
  // Distinct from the per-line accounting splits, which keep their own cost centers.
  costCenterId?: number
  // Employee to reimburse (a Provider with type === 'EMPLOYEE'). Sent as `recipient` + `recipient_type=EMPLOYEE`.
  beneficiaryId?: number
  // Beneficiary NATIONAL bank data — only sent when the policy exposes `provider_info` and the employee's
  // own bank data is incomplete. International banking is intentionally out of scope (employees are domestic).
  // Flat strings on purpose: the store resolves the combobox's `Bank` object (name + code) before building this.
  recipientBank?: string
  recipientBankCode?: string
  recipientAccountType?: string
  recipientAccountNumber?: string
  recipientEmail?: string
  recipientTaxId?: string
  // Always 'NATIONAL' when a beneficiary is set (no international branch in renditions).
  recipientNationalType?: string
  // Answers to the policy-defined dynamic form (step 3), parsed to the same shape the payment
  // request sends: each field carries its final `answer`. Empty when the policy has no form.
  formResponse: Field[]
  invoices: RenditionInvoice[]
  // ISO-2 country of the society: tells a Colombian withholding stored as a line tax from a tax.
  country?: string | null
}

/** Aggregate money of a rendición in the policy currency, as the backend computed it (preview or detail). */
export interface RenditionSummary {
  currency: string
  subtotal: number
  discount: number
  taxes: number
  withholdings: number
  tip: number
  total: number
}
